import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, LinearProgress, Stack, Typography, useTheme } from '@mui/material'

import { trackEvent } from 'analytics'

import useActions from '../use-actions'
import { buildImportRows, parseCsv } from './csv'
import ImportSkeleton from './ImportSkeleton'
import { loadingProgressSx } from './ImportStyles'

const parseError = (error) => {
  try {
    return JSON.stringify(error.graphQLErrors[0]['extensions'].response.body)
  } catch (e) {
    return error.message
  }
}
const StepImport = ({ data, ssidId, withPsk, onFinish }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState({ rooms: [], errors: [] })
  const { importRooms } = useActions()
  const spacesCount = parseCsv(data, withPsk).length
  const handleCreateFinish = async () => {
    setLoading(true)
    const { rooms = [], errors } = await importRooms(buildImportRows(data, withPsk), ssidId)
    trackEvent('private_wifi_imported', { item_count: rooms?.length ?? 0, error_count: errors?.length ?? 0 })
    setResult({ rooms, errors: errors?.map(parseError) || [] })
    setLoading(false)
  }
  useEffect(() => {
    handleCreateFinish()
      .then(onFinish)
      .catch(e => console.log(e))
  }, [])
  return (
    <Stack sx={{ gap: theme.spacing(2) }}>
      {loading ? (
        <Stack sx={{ gap: theme.spacing(1.5) }}>
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <Box sx={{ color: theme.palette.primary.main, display: 'flex', fontSize: 16 }}>
                <i className="fa-light fa-spinner-third fa-spin" />
              </Box>
              <Typography sx={{ color: '#292929', fontSize: 12, fontWeight: 600 }}>
                {t('ran.import.import_in_progress')}
              </Typography>
            </Stack>
            <Typography sx={{ color: '#7A8096', fontSize: 11 }}>
              {t('ran.import.spaces_count_to_create', { count: spacesCount })}
            </Typography>
          </Stack>
          <LinearProgress sx={loadingProgressSx} />
          <ImportSkeleton />
        </Stack>
      ) : (
        <Stack>
          <Stack sx={{ flexDirection: 'row', gap: theme.spacing(2) }}>
            <Stack
              sx={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                padding: theme.spacing(2),
                border: `1px solid ${theme.palette.grey['200']}`,
                borderRadius: theme.spacing(1.5),
                backgroundColor: '#F7FAFF',
                gap: theme.spacing(1.5)
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: '50%',
                  color: theme.palette.primary.main,
                  backgroundColor: '#DBE8FE'
                }}>
                <i className="fa-regular fa-circle-check" />
              </Box>
              <Typography sx={{ fontSize: 12, color: theme.palette.grey['700'] }}>
                {t('ran.import.added_spaces_count', { count: result.rooms.length })}
              </Typography>
            </Stack>
            <Stack
              sx={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                padding: theme.spacing(2),
                border: `1px solid ${result.errors?.length ? '#FEE2E2' : theme.palette.grey['200']}`,
                borderRadius: theme.spacing(1.5),
                gap: theme.spacing(1.5)
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: '50%',
                  color: result.errors.length ? theme.palette.error.main : theme.palette.grey['700'],
                  backgroundColor: result.errors.length ? '#FEF2F2' : theme.palette.grey['100']
                }}>
                <i className={`fa-regular fa-${result.errors?.length ? 'triangle-exclamation' : 'circle-check'}`} />
              </Box>
              <Typography sx={{ fontSize: 12, color: theme.palette.grey['700'] }}>
                {t('ran.import.errors_count', {
                  count: result.errors.length
                })}
              </Typography>
            </Stack>
          </Stack>
          <Stack>
            {
              Boolean(result.errors.length) && (
                <Stack mt={1}>
                  {
                    result.errors.map(error => <Box color={'error.main'} key={error} sx={{fontSize: 12}}>{error}</Box>)
                  }
                </Stack>
              )
            }
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default StepImport
